import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar } from '../../components/Calendar';
import { useCalendarKeyboard } from '../../components/Calendar/useCalendarKeyboard';
import type { DayData } from '../../components/Calendar/Calendar.types';
import { useScrollAreaTabStop } from '../../hooks/useScrollAreaTabStop';
import { formatDateISO, parseDateISO, todayISO } from '../../utils/dateHelpers';
import {
  ApplyButton,
  Footer,
  LoadMoreButton,
  MonthsContainer,
  Panel,
  ResetButton,
  StackedWeekdayLabel,
  StackedWeekdays,
} from './DatePicker.styles';
import type { DatePickerProps, SelectionPhase } from './DatePicker.types';

function startOfMonth(date: Date, monthOffset = 0): Date {
  return new Date(date.getFullYear(), date.getMonth() + monthOffset, 1);
}

function getDefaultWeekdays(): string[] {
  const wd = moment.localeData().weekdaysMin();
  return [...wd.slice(1), wd[0]].map((d) => d.charAt(0).toUpperCase());
}

function getMaxCheckoutDate(
  checkInDate: string,
  availability?: Map<string, DayData>
): string | null {
  if (!availability) return null;

  const start = parseDateISO(checkInDate);
  const maxLookahead = 365;

  for (let i = 1; i <= maxLookahead; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const ds = formatDateISO(d);
    const dayData = availability.get(ds);

    if (dayData && (dayData.type === 'unavailable' || dayData.type === 'hotel_closed')) {
      const prev = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i - 1);
      return formatDateISO(prev);
    }
  }

  return null;
}

export function DatePicker({
  checkIn: externalCheckIn,
  checkOut: externalCheckOut,
  singleDateMode = false,
  numberOfMonths = 2,
  availability,
  onRangeSelect,
  onSelectionChange,
  onMonthChange,
  onReset,
  onApply,
  columnView = false,
  showFooter = false,
  stacked: stackedProp = false,
  pageScroll = false,
  showPastDates = false,
  labels: l,
}: DatePickerProps) {
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 600px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 600px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const now = new Date();
  const currentMonth = startOfMonth(now);

  const stacked = stackedProp || isMobileViewport;
  const showPastDatesInGrid = showPastDates && !stacked;
  const [monthsToShow, setMonthsToShow] = useState<number>(numberOfMonths);
  const [phase, setPhase] = useState<SelectionPhase>('check-in');
  const [internalCheckIn, setInternalCheckIn] = useState<string | null>(externalCheckIn ?? null);
  const [internalCheckOut, setInternalCheckOut] = useState<string | null>(externalCheckOut ?? null);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  const [visibleMonth, setVisibleMonth] = useState<Date>(() => {
    if (!externalCheckIn) return currentMonth;
    const checkInMonth = startOfMonth(parseDateISO(externalCheckIn));
    return checkInMonth > currentMonth ? checkInMonth : currentMonth;
  });

  const visibleMonthCount = stacked ? monthsToShow : numberOfMonths;

  const reportVisibleMonths = (firstVisibleMonth: Date, monthCount = visibleMonthCount) => {
    onMonthChange?.(firstVisibleMonth, startOfMonth(firstVisibleMonth, monthCount - 1));
  };

  useEffect(() => {
    setInternalCheckIn(externalCheckIn ?? null);
    setInternalCheckOut(externalCheckOut ?? null);
  }, [externalCheckIn, externalCheckOut]);

  const maxCheckout =
    phase === 'check-out' && internalCheckIn
      ? getMaxCheckoutDate(internalCheckIn, availability)
      : null;

  const effAvailability = useMemo((): Map<string, DayData> | undefined => {
    if (!availability) return undefined;
    if (phase !== 'check-out' || !internalCheckIn || !maxCheckout) return availability;

    const map = new Map(availability);
    for (const [dateStr, data] of map) {
      if (dateStr > maxCheckout && data.type === 'available') {
        map.set(dateStr, { ...data, type: 'unavailable' });
      }
    }
    return map;
  }, [availability, phase, internalCheckIn, maxCheckout]);

  const firstBookableDate = useMemo(() => {
    if (!availability) return undefined;
    const today = todayISO();
    let earliest: string | undefined;
    for (const [dateStr, data] of availability) {
      if (dateStr < today) continue;
      if (data.type !== 'available') continue;
      if (!earliest || dateStr < earliest) earliest = dateStr;
    }
    return earliest;
  }, [availability]);

  const goToPrevMonth = () => {
    const prev = startOfMonth(visibleMonth, -1);
    if (prev >= startOfMonth(new Date())) {
      setVisibleMonth(prev);
      reportVisibleMonths(prev);
    }
  };

  const goToNextMonth = () => {
    const next = startOfMonth(visibleMonth, 1);
    setVisibleMonth(next);
    reportVisibleMonths(next);
  };

  const canGoPrev =
    visibleMonth.getFullYear() > now.getFullYear() ||
    (visibleMonth.getFullYear() === now.getFullYear() && visibleMonth.getMonth() > now.getMonth());

  const handleDayClick = (dateStr: string) => {
    const today = todayISO();
    if (dateStr < today) return;

    if (singleDateMode) {
      setInternalCheckIn(dateStr);
      setInternalCheckOut(null);
      onSelectionChange?.(dateStr, dateStr);
      onRangeSelect(dateStr, dateStr);
      return;
    }

    if (phase === 'check-in' || dateStr <= internalCheckIn!) {
      setInternalCheckIn(dateStr);
      setInternalCheckOut(null);
      setPhase('check-out');
      onSelectionChange?.(dateStr, null);
    } else {
      setInternalCheckOut(dateStr);
      setPhase('check-in');
      onSelectionChange?.(internalCheckIn!, dateStr);
      onRangeSelect(internalCheckIn!, dateStr);
    }
  };

  const monthsRef = useRef<HTMLDivElement>(null);
  useCalendarKeyboard(monthsRef);
  useScrollAreaTabStop(monthsRef);

  const weekdaysForHeader = l?.weekdays ?? getDefaultWeekdays();
  const calendarLabels = l
    ? { weekdays: l.weekdays, months: l.months, checkIn: l.checkIn, checkOut: l.checkOut }
    : undefined;

  const isDouble = numberOfMonths > 1;
  const monthList = stacked
    ? Array.from({ length: monthsToShow }, (_, i) => startOfMonth(visibleMonth, i))
    : [visibleMonth, ...(isDouble ? [startOfMonth(visibleMonth, 1)] : [])];

  const handleLoadEarlier = () => {
    const prev = startOfMonth(visibleMonth, -1);
    if (prev < currentMonth) return;
    const nextCount = monthsToShow + 1;
    setVisibleMonth(prev);
    setMonthsToShow(nextCount);
    reportVisibleMonths(prev, nextCount);
  };

  const handleLoadMore = () => {
    const nextCount = monthsToShow + 1;
    reportVisibleMonths(visibleMonth, nextCount);
    setMonthsToShow(nextCount);
  };

  return (
    <Panel
      $columnView={columnView}
      $singleMonth={numberOfMonths === 1}
      $stacked={stacked}
      $pageScroll={pageScroll && stacked}
    >
      {stacked && (
        <StackedWeekdays data-sticky={pageScroll}>
          {weekdaysForHeader.map((wd, i) => (
            <StackedWeekdayLabel key={i}>{wd}</StackedWeekdayLabel>
          ))}
        </StackedWeekdays>
      )}
      <MonthsContainer
        ref={monthsRef}
        $columnView={columnView}
        $singleMonth={numberOfMonths === 1}
        $stacked={stacked}
        $pageScroll={pageScroll && stacked}
      >
        {stacked && canGoPrev && (
          <LoadMoreButton type="button" onClick={handleLoadEarlier}>
            {l?.loadEarlier ?? 'Load earlier dates'}
          </LoadMoreButton>
        )}
        {monthList.map((m, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === monthList.length - 1;
          return (
            <Calendar
              key={`${m.getFullYear()}-${m.getMonth()}`}
              year={m.getFullYear()}
              month={m.getMonth()}
              availability={effAvailability}
              checkIn={internalCheckIn}
              checkOut={internalCheckOut}
              isSelectingCheckOut={phase === 'check-out'}
              hoveredDate={hoveredDate}
              onDayClick={handleDayClick}
              onDayHover={setHoveredDate}
              onPrevMonth={!stacked && isFirst ? goToPrevMonth : undefined}
              prevMonthDisabled={!canGoPrev}
              onNextMonth={!stacked && isLast ? goToNextMonth : undefined}
              hideWeekdays={stacked}
              labels={calendarLabels}
              firstBookableDate={firstBookableDate}
              showPastDates={showPastDatesInGrid}
            />
          );
        })}
        {stacked && (
          <LoadMoreButton type="button" onClick={handleLoadMore}>
            {l?.loadMore ?? 'Load more'}
          </LoadMoreButton>
        )}
      </MonthsContainer>

      {showFooter && (
        <Footer>
          <ResetButton
            type="button"
            onClick={onReset}
            disabled={!onReset || (!internalCheckIn && !internalCheckOut)}
          >
            {l?.reset ?? 'Reset'}
          </ResetButton>
          <ApplyButton
            type="button"
            onClick={onApply}
            disabled={!onApply || !internalCheckIn || (!singleDateMode && !internalCheckOut)}
          >
            {l?.apply ?? 'Apply'}
          </ApplyButton>
        </Footer>
      )}
    </Panel>
  );
}
